import type { SupplyAcceptance } from "./supply-acceptance";

export interface SupplyTransitDetail {
  orderId: string | null;        // supply order id, if Ozon returned it
  orderNumber?: string | null;   // human-facing Ozon supply order number
  supplyId?: string | null;      // human-facing Ozon supply id, if present
  bundleId: string;              // bundle id used to fetch items
  quantity: number;              // qty of this offer_id in this in-transit bundle
  acceptance?: SupplyAcceptance; // published acts; accepted != available for sale
  acceptanceReadFailed?: boolean;
  expectedDate: string | null;   // expected arrival (timeslot.from ISO)
  status?: string | null;        // Ozon supply status
  sourceName?: string | null;    // source point/warehouse, if Ozon returned it
  warehouseName: string | null;  // destination warehouse name, if available
  warehouseId?: string | null;   // числовой id склада назначения (storage_warehouse.warehouse_id) — запасной ключ кластера, когда имя пустое
  destCluster?: string | null;   // кластер НАЗНАЧЕНИЯ по macrolocal_cluster_id (проверено 2026-07-06: 15/15 совпадений со storage_warehouse; главный ключ у «слепых» кросс-доков, где склада нет)
  crossdock?: boolean;           // кросс-док поставка (is_crossdock): сдают в хаб, Ozon развозит в кластер назначения
  virtual?: boolean;             // виртуальная поставка (order_tags.is_virtual) — через вРЦ/распределение
  createdDate?: string | null;   // дата создания заявки (order.created_date) — реальная, для честной длины полосы
  statusDate?: string | null;    // дата последнего изменения статуса (order.state_updated_date) — когда перешло в текущий статус
}

export interface SupplyStats {
  lastDate: string | null;       // ISO date of last completed supply
  lastQty: number;               // qty in last supply
  daysSinceLast: number | null;  // days since last supply
  count30d: number;              // completed supplies last 30d
  count90d: number;              // completed supplies last 90d
  total30d: number;              // total qty delivered last 30d
  total90d: number;              // total qty delivered last 90d
  inTransitQty: number;          // qty currently physically in transit
  inTransitDate: string | null;  // expected arrival (timeslot.from ISO)
  inTransitDetails?: SupplyTransitDetail[]; // per-bundle in-transit details for tooltips
  receivingQty?: number;         // qty already at Ozon and currently being accepted
  receivingDate?: string | null; // earliest receiving-related timeslot
  receivingDetails?: SupplyTransitDetail[]; // per-bundle receiving details
  requestQty?: number;           // qty in created supply requests that are not shipped yet
  requestDetails?: SupplyTransitDetail[]; // per-bundle requested supply details
  completedDetails?: SupplyTransitDetail[]; // ЗАВЕРШЁННЫЕ поставки (status COMPLETED, ~30 дн) — для колонки «Принято»
  lastWarehouse: string | null;  // warehouse name for cluster lookup
  dailySupplies: number[];       // поставок по дням: 30 значений, index 0 = 30 дней назад, index 29 = вчера
}

export type SupplyMap = Record<string, SupplyStats>; // offer_id → stats

export const ACTIVE_SUPPLY_TRANSIT_STATES = new Set(["IN_TRANSIT"]);
export const RECEIVING_SUPPLY_STATES = new Set(["ACCEPTANCE_AT_STORAGE_WAREHOUSE"]);
export const REQUESTED_SUPPLY_STATES = new Set(["READY_TO_SUPPLY"]);
// «Согласование актов» — после приёмки, ждёт подтверждения актов. Единицы УЖЕ приняты (в остатке).
// Отслеживаем, чтобы показать в канбане/таймлайне (иначе поставка «на согласовании» была невидимой).
export const REPORTS_CONFIRMATION_STATES = new Set(["REPORTS_CONFIRMATION_AWAITING"]);
export const TRACKED_ACTIVE_SUPPLY_STATES = new Set([...REQUESTED_SUPPLY_STATES, ...ACTIVE_SUPPLY_TRANSIT_STATES, ...RECEIVING_SUPPLY_STATES, ...REPORTS_CONFIRMATION_STATES]);

type SupplyTransitLike = {
  inTransitQty?: number;
  inTransitDate?: string | null;
  inTransitDetails?: SupplyTransitDetail[];
  receivingQty?: number;
  receivingDate?: string | null;
  receivingDetails?: SupplyTransitDetail[];
  requestQty?: number;
  requestDetails?: SupplyTransitDetail[];
};

export function isActiveSupplyTransitStatus(status: string | null | undefined): boolean {
  return ACTIVE_SUPPLY_TRANSIT_STATES.has(String(status ?? "").trim().toUpperCase());
}

// Кэш по ссылке на объект stats: кластерные ячейки зовут эти функции по разу на
// КАЖДЫЙ город (результат от города не зависит) — на каждый рендер при скролле это
// были тысячи лишних .filter(). Данные между рендерами стабильны по ссылке, при
// обновлении приходит новый объект → кэш сам инвалидируется. WeakMap не держит
// память. (То же для receiving/requested и группировки по кластеру ниже.)
const EMPTY_DETAILS: SupplyTransitDetail[] = [];
const activeTransitCache = new WeakMap<object, SupplyTransitDetail[]>();
const receivingCache = new WeakMap<object, SupplyTransitDetail[]>();
const requestedCache = new WeakMap<object, SupplyTransitDetail[]>();
const byClusterCache = new WeakMap<SupplyTransitDetail[], Map<string, SupplyTransitDetail[]>>();

export function getActiveSupplyTransitDetails(stats: SupplyTransitLike | null | undefined): SupplyTransitDetail[] {
  if (!stats) return EMPTY_DETAILS;
  const cached = activeTransitCache.get(stats);
  if (cached) return cached;
  const result = (stats.inTransitDetails ?? []).filter(detail => detail.quantity > 0 && isActiveSupplyTransitStatus(detail.status));
  activeTransitCache.set(stats, result);
  return result;
}

export function getActiveSupplyTransitQty(stats: SupplyTransitLike | null | undefined): number {
  const details = stats?.inTransitDetails;
  if (details?.length) {
    return getActiveSupplyTransitDetails(stats).reduce((sum, detail) => sum + detail.quantity, 0);
  }
  return stats?.inTransitQty ?? 0;
}

export function getActiveSupplyTransitDate(stats: SupplyTransitLike | null | undefined): string | null {
  const details = getActiveSupplyTransitDetails(stats)
    .filter(detail => detail.expectedDate)
    .sort((a, b) => String(a.expectedDate).localeCompare(String(b.expectedDate)));
  if (details.length) return details[0].expectedDate;
  return stats?.inTransitDetails?.length ? null : stats?.inTransitDate ?? null;
}

export function isReceivingSupplyStatus(status: string | null | undefined): boolean {
  return RECEIVING_SUPPLY_STATES.has(String(status ?? "").trim().toUpperCase());
}

export function getReceivingSupplyDetails(stats: SupplyTransitLike | null | undefined): SupplyTransitDetail[] {
  if (!stats) return EMPTY_DETAILS;
  const cached = receivingCache.get(stats);
  if (cached) return cached;
  const explicit = (stats.receivingDetails ?? []).filter(detail => detail.quantity > 0 && isReceivingSupplyStatus(detail.status));
  const result = explicit.length
    ? explicit
    : (stats.inTransitDetails ?? []).filter(detail => detail.quantity > 0 && isReceivingSupplyStatus(detail.status));
  receivingCache.set(stats, result);
  return result;
}

export function getReceivingSupplyQty(stats: SupplyTransitLike | null | undefined): number {
  const details = getReceivingSupplyDetails(stats);
  if (details.length) return details.reduce((sum, detail) => sum + detail.quantity, 0);
  return stats?.receivingQty ?? 0;
}

export function getReceivingSupplyDate(stats: SupplyTransitLike | null | undefined): string | null {
  const details = getReceivingSupplyDetails(stats)
    .filter(detail => detail.expectedDate)
    .sort((a, b) => String(a.expectedDate).localeCompare(String(b.expectedDate)));
  if (details.length) return details[0].expectedDate;
  return stats?.receivingDate ?? null;
}

export function isRequestedSupplyStatus(status: string | null | undefined): boolean {
  return REQUESTED_SUPPLY_STATES.has(String(status ?? "").trim().toUpperCase());
}

export function getRequestedSupplyDetails(stats: SupplyTransitLike | null | undefined): SupplyTransitDetail[] {
  if (!stats) return EMPTY_DETAILS;
  const cached = requestedCache.get(stats);
  if (cached) return cached;
  const result = (stats.requestDetails ?? []).filter(detail => detail.quantity > 0 && isRequestedSupplyStatus(detail.status));
  requestedCache.set(stats, result);
  return result;
}

export function getRequestedSupplyQty(stats: SupplyTransitLike | null | undefined): number {
  const details = getRequestedSupplyDetails(stats);
  if (details.length) return details.reduce((sum, detail) => sum + detail.quantity, 0);
  return stats?.requestQty ?? 0;
}

// Регистронезависимый индекс карты складов (строится один раз на объект-карту).
// Нужен, т.к. Ozon отдаёт склад поставки ЗАГЛАВНЫМИ («ЕКАТЕРИНБУРГ_РФЦ_НОВЫЙ»), а справочник
// кластеров — с заглавной только город («Екатеринбург_РФЦ_НОВЫЙ»); прямое сравнение их не ловит.
// JS toUpperCase корректно приводит кириллицу (в отличие от SQLite upper/lower).
const CLUSTER_MAP_UPPER = new WeakMap<Record<string, string>, Record<string, string>>();
function upperClusterIndex(clusterMap: Record<string, string>): Record<string, string> {
  let idx = CLUSTER_MAP_UPPER.get(clusterMap);
  if (!idx) {
    idx = {};
    for (const key in clusterMap) { const u = key.toUpperCase(); if (!(u in idx)) idx[u] = clusterMap[key]; }
    CLUSTER_MAP_UPPER.set(clusterMap, idx);
  }
  return idx;
}

// «Маркерные» токены имени склада → кластер: токен, который в справочнике этого магазина
// встречается РОВНО в одном кластере (строится один раз на карту). Общие токены (РФЦ, СЦ, ХАБ…)
// встречаются в разных кластерах → помечаются как неоднозначные (null) и в запасном не используются.
const CLUSTER_TOKEN_MARKER = new WeakMap<Record<string, string>, Record<string, string | null>>();
function clusterTokenMarkers(clusterMap: Record<string, string>): Record<string, string | null> {
  let idx = CLUSTER_TOKEN_MARKER.get(clusterMap);
  if (!idx) {
    idx = {};
    for (const key in clusterMap) {
      const cluster = clusterMap[key];
      for (const tok of key.toUpperCase().split("_")) {
        if (!tok || /^\d+$/.test(tok)) continue;         // пропускаем пустые и числовые (ID склада)
        if (!(tok in idx)) idx[tok] = cluster;
        else if (idx[tok] !== cluster) idx[tok] = null;  // токен в разных кластерах → неоднозначный
      }
    }
    CLUSTER_TOKEN_MARKER.set(clusterMap, idx);
  }
  return idx;
}

export function clusterForWarehouseName(name: string | null | undefined, clusterMap: Record<string, string>): string | null {
  const warehouse = name?.trim();
  if (!warehouse) return null;

  const candidates = [warehouse];
  // Ozon sometimes returns the base RFC name in supply orders, while the
  // cluster dictionary only contains its cross-docking variant.
  if (!warehouse.toUpperCase().endsWith("_КРОССДОКИНГ")) {
    candidates.push(`${warehouse}_Кроссдокинг`, `${warehouse}_КРОССДОКИНГ`);
  }

  const idx = upperClusterIndex(clusterMap);
  for (const candidate of candidates) {
    const cluster = clusterMap[candidate] ?? idx[candidate.toUpperCase()]; // точное совпадение, затем регистронезависимо
    if (cluster) return cluster;
  }

  // Запасное: склада нет в справочнике (Ozon не отдал этот вариант, напр. «ТВЕРЬ_РФЦ_ЮВЕЛИРНЫЙ»).
  // Определяем кластер по маркерному токену имени (напр. «ТВЕРЬ» → Тверь); неоднозначные токены пропускаем.
  const markers = clusterTokenMarkers(clusterMap);
  for (const tok of warehouse.toUpperCase().split("_")) {
    const cluster = markers[tok];
    if (cluster) return cluster;
  }

  return null;
}

// Кластер по числовому id склада (storage_warehouse.warehouse_id). Справочник содержит и числовые
// ключи складов — так ловим кластер назначения, когда Ozon не заполнил ИМЯ склада (частый кейс кросс-дока).
export function clusterForWarehouseId(id: string | number | null | undefined, clusterMap: Record<string, string>): string | null {
  if (id == null || id === "" || id === 0) return null;
  return clusterMap[String(id)] ?? null;
}

export function clusterForSupplyDetail(detail: SupplyTransitDetail, clusterMap: Record<string, string>): string | null {
  // Приоритет: склад назначения → кластер назначения (macrolocal) → хаб сдачи.
  // Хаб — последний резерв: сдать можно в Софьино, а везти в СПб (реальный кейс).
  return (
    clusterForWarehouseName(detail.warehouseName, clusterMap) ??
    clusterForWarehouseId(detail.warehouseId, clusterMap) ??
    detail.destCluster ??
    clusterForWarehouseName(detail.sourceName, clusterMap) ??
    detail.warehouseName?.trim() ??
    detail.sourceName?.trim() ??
    null
  );
}

export function getSupplyDetailsForCluster(details: SupplyTransitDetail[], clusterName: string, clusterMap: Record<string, string>): SupplyTransitDetail[] {
  if (!details.length) return EMPTY_DETAILS;
  // Группируем детали по кластеру ОДИН раз на список (а не фильтруем по каждому
  // городу): для вида с кластерными колонками по 10+ городам это было 10+ проходов
  // по списку на ячейку × строки × кадр скролла. Кэш по ссылке на details (она
  // стабильна — из кэшированных get*Details выше).
  let byCluster = byClusterCache.get(details);
  if (!byCluster) {
    byCluster = new Map<string, SupplyTransitDetail[]>();
    for (const detail of details) {
      const cluster = clusterForSupplyDetail(detail, clusterMap);
      if (!cluster) continue;
      const arr = byCluster.get(cluster);
      if (arr) arr.push(detail);
      else byCluster.set(cluster, [detail]);
    }
    byClusterCache.set(details, byCluster);
  }
  return byCluster.get(clusterName) ?? EMPTY_DETAILS;
}

export function getSupplyQtyForCluster(details: SupplyTransitDetail[], clusterName: string, clusterMap: Record<string, string>): number {
  return getSupplyDetailsForCluster(details, clusterName, clusterMap).reduce((sum, detail) => sum + detail.quantity, 0);
}
